import { useMemo } from 'react';
import Drawer from '../../Components/Drawer';
import Portal from '../../Components/Portal';
import { getChestIcon } from '../../Components/utils/getChestIcon';
import RocketIcon from '../../icons/Rocket';
import USDTIcon from '../../icons/USDT';
import {
  TChestResponse, TChestReward, TChestRewardVariant,
} from '../../types';
import { getChestContainIcon } from './utils/getChestContainIcon';

type TChestContainsModalProps = {
    chest?: TChestResponse;
    onClose: () => void;
    open: boolean
}

type TChestDetailsValue = {
    from: number;
    to: number;
}

type TChestDetails = {
    chance: TChestDetailsValue;
    rocketCoins: TChestDetailsValue;
    usdt: TChestDetailsValue
}

type TChestsDetails = {
    [V in TChestRewardVariant]: TChestDetails
}

const chestIconOptions = {
  launch: {
    classNames: {
      chest: {
        wrapper: 'relative transform scale-x-[-1] transform translate-y-[10px] top-0 flex justify-center z-[1] max-h-[134px] max-w-[134px]',
      },
      glow: {
        wrapper: 'absolute top-0 left-[50%] transform translate-x-[-50%] translate-y-[10%] scale-[1.6]',
      },
      shine: {
        wrapper: 'absolute top-0 translate-y-[-23%] left-[50%] translate-x-[-50%] h-full z-[-1] scale-[1.3] max-w-[299px] w-full',
      },
      wrapper: 'flex justify-center',
    },
    glow: true,
    shine: true,
  },
  tutorial: {
    classNames: {
      chest: {
        wrapper: 'relative transform scale-x-[-1] transform translate-y-[10px] top-0 flex justify-center z-[1] max-h-[134px] max-w-[134px]',
      },
      glow: {
        wrapper: 'absolute top-0 left-[50%] transform translate-x-[-50%] translate-y-[10%] scale-[1.6]',
      },
      shine: {
        wrapper: 'absolute top-0 translate-y-[-23%] left-[50%] translate-x-[-50%] h-full z-[-1] scale-[1.3] max-w-[299px] w-full',
      },
      wrapper: 'flex justify-center',
    },
    glow: true,
    shine: true,
  },
  orbit: {
    classNames: {
      chest: {
        wrapper: 'relative transform scale-x-[-1] transform translate-y-[10px] top-0 flex justify-center z-[1] max-h-[134px] max-w-[134px]',
      },
      glow: {
        wrapper: 'absolute top-0 left-[50%] transform translate-x-[-50%] translate-y-[-10%] scale-[1.6]',
      },
      shine: {
        wrapper: 'absolute top-0 translate-y-[-23%] left-[50%] translate-x-[-50%] h-full z-[-1] max-w-[299px] w-full',
      },
      wrapper: 'flex justify-center',
    },
    shine: true,
  },
  galaxy: {
    classNames: {
      chest: {
        wrapper: 'relative transform scale-x-[-1] transform translate-y-[10px] top-0 flex justify-center z-[1] max-h-[134px] max-w-[134px]',
      },
      glow: {
        wrapper: 'absolute top-0 left-[50%] transform translate-x-[-50%] translate-y-[-10%] scale-[1.6]',
      },
      shine: {
        wrapper: 'absolute top-0 translate-y-[-23%] left-[50%] translate-x-[-50%] h-full z-[-1] max-w-[299px] w-full',
      },
      wrapper: 'flex justify-center',
    },
    glow: true,
    shine: true,
  },
  supernova: {
    classNames: {
      chest: {
        wrapper: 'relative transform scale-x-[-1] transform translate-y-[10px] top-0 flex justify-center z-[1] max-h-[134px] max-w-[134px]',
      },
      glow: {
        wrapper: 'absolute top-0 left-[50%] transform translate-x-[-50%] translate-y-[-10%] scale-[1.6]',
      },
      shine: {
        wrapper: 'absolute top-0 translate-y-[-23%] left-[50%] translate-x-[-50%] h-full z-[-1] max-w-[299px] w-full',
      },
      wrapper: 'flex justify-center',
    },
    glow: true,
    shine: true,
  },
};

function ChestContainsModal({ chest, onClose, open }: TChestContainsModalProps) {
  const rewardsDetails = useMemo(() => {
    if (!chest?.chest_rewards) {
      return {} as TChestsDetails;
    }

    const grouped = chest.chest_rewards.reduce((dict, r) => {
      if (!dict[r.variant]) {
        dict[r.variant] = [r];
      } else {
        dict[r.variant].push(r);
      }

      return dict;
    }, {} as {[V in TChestRewardVariant]: TChestReward[]});

    const details = Object.entries(grouped).reduce((dict, [variant, list]) => {
      const chances: number[] = [];
      const rocketCoins: number[] = [];
      const usdts: number[] = [];

      list.forEach((r) => {
        chances.push(r.chance);
        rocketCoins.push(r.rewards.rocketCoins);
        usdts.push(r.rewards.usdt);
      });

      const d: TChestDetails = {
        chance: {
          from: Math.min(...chances),
          to: Math.max(...chances),
        },
        rocketCoins: {
          from: Math.min(...rocketCoins),
          to: Math.max(...rocketCoins),
        },
        usdt: {
          from: Math.min(...usdts),
          to: Math.max(...usdts),
        },
      };

      return {
        ...dict,
        [variant]: d,
      };
    }, {} as TChestsDetails);

    return {
      legendary: details.legendary, epic: details.epic, rare: details.rare, common: details.common,
    };
  }, [chest?.chest_rewards]);

  return (
    <Portal>
      <Drawer
        side="bottom"
        onClose={onClose}
        open={open && !!chest}
        wrapperClassName={`flex flex-col shadow-xl rounded-t-[20px] z-[9] relative bg-${chest?.variant ?? 'launch'}ChestContainsModalFullfilledBg overflow-hidden`}
        closeStroke="[#ffffff]"
      >
        {!!chest && (
        <div className={`flex flex-col items-center px-4 pb-[52px] bg-${chest?.variant ?? 'launch'}ChestContainsModalBg`}>
          <div className="w-full h-full">
            {getChestIcon(chest.variant, chestIconOptions[chest.variant])}
          </div>
          <div className="relative z-[1] flex flex-col items-center">
            <div className="font-Bangers text-[22px] text-[#ffffff] pt-2 mt-[144px]">
              {chest.variant}
              {' '}
              chest
            </div>
            <span className="text-sm text-[#9C9DA9] text-center font-Barlow leading-[0.95rem]">{chest.description}</span>
            <div className="grid grid-cols-2 gap-2 w-full mt-4">
              {Object.entries(rewardsDetails).map(([variant, details]) => (
                !!details && (
                <div
                  key={variant}
                  className={`
                  aspect-[1/1]
                  font-Bangers
                  text-[#fff]
                  flex flex-col justify-between
                  px-4 py-2
                  bg-${variant}ChestContainBg
                  rounded-[12px]
                  gradient-border cyan z-[1] before:rounded-[12px]
                  relative
                  overflow-hidden
              `}
                >

                  <span className="text-xs">
                    Chance
                    {' '}
                    {details.chance.from === details.chance.to ? `${details.chance.from}%` : `${details.chance.from}% - ${details.chance.to}%`}
                  </span>
                  <div className="flex justify-around">
                    <div className="flex flex-col items-center">
                      <USDTIcon size={24} />
                      <span>
                        {details.usdt.from === details.usdt.to ? details.usdt.from : `${details.usdt.from} - ${details.usdt.to}`}
                      </span>
                    </div>
                    <div className="flex flex-col items-center">
                      <RocketIcon size={24} />
                      <span>
                        {details.rocketCoins.from === details.rocketCoins.to ? details.rocketCoins.from : `${details.rocketCoins.from} - ${details.rocketCoins.to}`}
                      </span>
                    </div>
                  </div>
                  <div className="absolute flex items-center justify-center inset-0 w-full h-full z-[-1]">
                    {getChestContainIcon(variant as TChestRewardVariant)}
                  </div>
                </div>
                )
              ))}
            </div>
          </div>
        </div>
        )}
      </Drawer>
    </Portal>
  );
}

export default ChestContainsModal;
